// web/src/components/layout/Shell.tsx
import type { ReactNode } from "react";

import type { Me } from "../../lib/api";
import { Annotator } from "../Annotator";

const LINKS = [
  { key: "home", href: "/", label: "Главная" },
  { key: "stock", href: "/stock", label: "Сток" },
] as const;

/**
 * Каркас кабинета.
 *
 * Боковая навигация появилась вместе со вторым разделом: до него её было
 * не из чего строить, и меню из одного пункта было бы украшением.
 *
 * Ссылки обычные, а не клиентский роутер: разделов два, адреса ровно два, и
 * маршрутизатор здесь был бы узлом, который надо помнить и обновлять.
 *
 * Аннотатор живёт здесь, а не на каждом экране: каркас общий для «Главной» и
 * «Стока», значит кнопка разметки есть везде, где есть что размечать. Экран
 * входа каркасом не обёрнут — там одно поле пароля, и кнопка в углу была бы
 * приглашением, адресованным не тому.
 */
export function Shell({
  me,
  onLeave,
  active,
  children,
}: {
  me: Me;
  onLeave: () => void;
  active: "home" | "stock";
  children: ReactNode;
}) {
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-bg">
      <header
        className="flex h-[var(--spacing-topbar)] flex-none items-center gap-3 border-b border-border px-5"
        data-annot="шапка"
      >
        <div className="grid size-[22px] place-items-center rounded-[5px] bg-fg text-[12px] font-semibold text-bg">
          f
        </div>
        <div className="text-[13px] font-medium text-fg">fbsozon</div>
        <div className="ml-auto flex items-center gap-3 text-[13px]">
          <span className="text-fg-muted">{me.name}</span>
          <button
            type="button"
            onClick={onLeave}
            className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-fg-faint hover:bg-bg-hover hover:text-fg"
            data-annot="выйти"
          >
            Выйти
          </button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <nav
          className="flex w-44 flex-none flex-col gap-0.5 border-r border-border p-2 text-[13px]"
          data-annot="меню"
        >
          {LINKS.map((link) => (
            <a
              key={link.key}
              href={link.href}
              className={`h-8 rounded-[var(--radius-ui)] px-2 leading-8 ${
                active === link.key
                  ? "bg-bg-hover text-fg"
                  : "text-fg-faint hover:text-fg"
              }`}
              data-annot={link.label}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
      <Annotator />
    </div>
  );
}
